import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms'
import {distinctUntilChanged, of, Subject, Subscription} from "rxjs";
import {map, take} from "rxjs/operators";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {SelectOption} from '../form-group/form-group.component';
import {NgMultiSelectDropDownModule} from 'ng-multiselect-dropdown';
import {TranslatePipe} from '@ngx-translate/core';
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {ListItem} from '../../../entities/list-item.entity';

@Component({
  selector: 'app-multi-select-with-static-options[selectOptions]',
  templateUrl: './multi-select-with-static-options-static.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: MultiSelectWithStaticOptionsStaticComponent,
    multi: true
  }],
  imports: [TooltipModule, FormsModule, NgMultiSelectDropDownModule, TranslatePipe, NgIf, KeyValuePipe, NgForOf],
  standalone: true
})
export class MultiSelectWithStaticOptionsStaticComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  @Input() public label!: string
  @Input() public small = false
  @Input() public inline = false
  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public sortByDisplayValue = false
  @Input() public prefix?: string
  @Input() public selectOptions: SelectOption[] = []

  @Input() public multi = true
  @Input() public returnAsArray = false

  @Input() public tooltipText?: string

  @Input() public placeholder?: string;

  public selectedItems: ListItem[] = []
  /**
   * @see https://github.com/nileshpatel17/ng-multiselect-dropdown#settings
   */
  public multiSelectSearchFilterChanged: EventEmitter<string> = new EventEmitter<string>()
  @Input() public multiSelectIsLoading = false;
  public allSelectOptions: ListItem[] = [];
  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;
  @Input()
  public formControl?: FormControl<string[] | string | null | undefined>
  @Input()
  public formControlName!: string;
  protected readonly Infinity = Infinity;
  private _onChangeChanged: Subject<(v: string[] | string | null | undefined) => void> = new Subject<(v: string[] | string | null | undefined) => void>();
  private registerOnChangeControlValueChangesSubscription?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef,
              private readonly injector: Injector) {
  }

  public get control(): FormControl<string[] | string | null | undefined> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <FormControl<string[] | string | null | undefined>>this.formControl ?? this.controlContainer.control.get(this.formControlName);
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  public selectionChanged() {

    let val: string[] | string | null
    if (!this.multi) {
      if (!this.returnAsArray) {
        val = this.selectedItems.length === 1 ? (this.selectedItems[0].id as string) : null;
      } else {
        val = this.selectedItems.map(d => {
          return d.id.toString();
        })
      }


    } else {
      val = this.selectedItems.map(d => {
        return d.id.toString();
      })
    }
    this.control.setValue(val)

  }

  public updateSelectedItems() {

    const currentlySelected = this.control.getRawValue()

    if (!currentlySelected || currentlySelected.length === 0) {
      // aktuell nichts ausgewählt
      this.selectedItems = [];
      this.cdr.markForCheck();
      return
    } else if (typeof currentlySelected === 'string') {
      // eine id ausgewählt
      const selectedListItem = this.allSelectOptions.find(option => {
        return option.id.toString() === currentlySelected;
      });
      this.selectedItems = selectedListItem ? [selectedListItem] : [];
      this.cdr.markForCheck();
      return;
    }

    if (currentlySelected.length === 0) {
      this.cdr.markForCheck();
      return;
    }

    this.selectedItems = this.allSelectOptions.filter((option) => {
      return currentlySelected.includes(option.id.toString());
    })

    this.cdr.detectChanges();
  }

  public ngOnInit() {

    this.control.valueChanges.subscribe(
      () => {
        this.updateSelectedItems();
      }
    )

    of(this.selectOptions).pipe(
      take(1),
      map((list) => {

        if (this.sortByDisplayValue) {
          // Custom sorting function to handle null values
          const customSort = (a: Pick<SelectOption, 'value'>, b: Pick<SelectOption, 'value'>) => {
            const textA = a.value ?? '';
            const textB = b.value ?? '';
            if (typeof textA === 'string' && typeof textB === 'string') {
              return textA.localeCompare(textB);
            } else {
              return textA.toString().localeCompare(textB.toString())
            }
          };


          // Sort the list of options using the custom sorting function
          list.sort(customSort);
        }

        return list.map((option) => {
            const listItem: ListItem = {
              id: option.key,
              text: option.value ?? '',
              isDisabled: option.disabled
            }
            return listItem;
          }
        )
      }),
    )
      .subscribe(
        {

          next: (allModels) => {

            this.allSelectOptions = allModels;
            this.updateSelectedItems()

          },
          error: () => {
            this.multiSelectIsLoading = false;
            this.control.setErrors({
              serverError: 'Invalid Data received from Server!'
            }, {emitEvent: true})
            this.control.markAsDirty()
            this.control.markAsTouched()

          }
        }
      )

    this.multiSelectSearchFilterChanged?.next('');

    this._onChangeChanged.subscribe(
      fn => {
        this.registerOnChangeControlValueChangesSubscription?.unsubscribe();
        this.registerOnChangeControlValueChangesSubscription =
          this.control.valueChanges.pipe(distinctUntilChanged()).subscribe(
            value => {
              fn(value)
            }
          );
      }
    );
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      'selectOptions' in changes &&
      changes['selectOptions'] &&
      JSON.stringify(changes['selectOptions'].previousValue) !== JSON.stringify(changes['selectOptions'].currentValue)
    ) {
      of(this.selectOptions).pipe(
        take(1),
        map((list) => {

          if (this.sortByDisplayValue) {
            // Custom sorting function to handle null values
            const customSort = (a: Pick<SelectOption, 'value'>, b: Pick<SelectOption, 'value'>) => {
              const textA = a.value ?? '';
              const textB = b.value ?? '';
              if (typeof textA === 'string' && typeof textB === 'string') {
                return textA.localeCompare(textB);
              } else {
                return textA.toString().localeCompare(textB.toString())
              }
            };


            // Sort the list of options using the custom sorting function
            list.sort(customSort);
          }

          return list.map((option) => {
              const listItem: ListItem = {
                id: option.key,
                text: option.value ?? '',
                isDisabled: option.disabled
              }
              return listItem;
            }
          )
        }),
      )
        .subscribe(
          {

            next: (allModels) => {

              this.allSelectOptions = allModels;
              this.updateSelectedItems()

            },
            error: () => {
              this.multiSelectIsLoading = false;
              this.control.setErrors({
                serverError: 'Invalid Data received from Server!'
              }, {emitEvent: true})
              this.control.markAsDirty()
              this.control.markAsTouched()

            }
          }
        )
    }
  }

  public ngOnDestroy() {
    this.registerOnChangeControlValueChangesSubscription?.unsubscribe();
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: (v: string[] | string | null | undefined) => void): void {
    this._onChangeChanged.next(fn);
  }

  writeValue(obj: string[]): void {
    if (this.control.value !== obj) {
      this.control.setValue(obj, {emitEvent: true});
    }
    this.updateSelectedItems();
    this.cdr.detectChanges();


  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }


}
